<script setup>
import dayjs from 'dayjs'
import { ref, onMounted } from 'vue'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useChips } from '@/composables/chips'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import TaskProgressSummary from '@/components/task/TaskProgressSummary.vue'

const props = defineProps({
  item: {
    type: Object,
    default: () => {},
  },
  icon: {
    type: String,
    required: false,
    default: 'mdi-checkbox-blank-circle',
  },
  clickable: {
    type: Boolean,
    required: false,
    default: true,
  },
  hideColor: {
    type: Boolean,
    required: false,
  },
  hideDate: {
    type: Boolean,
    required: false,
  },
  hideTime: {
    type: Boolean,
    required: false,
  },
  hideLabel: {
    type: Boolean,
    required: false,
  },
  hideStatus: {
    type: Boolean,
    required: false,
  },
  statusLeft: {
    type: Boolean,
    required: false,
  },
  date: {
    type: String,
    required: false,
  },
  start: {
    type: String,
    required: false,
  },
  end: {
    type: String,
    required: false,
  },
  showStaff: {
    type: Boolean,
    required: false,
  },
  showEquipment: {
    type: Boolean,
    required: false,
  },
  showTags: {
    type: Boolean,
    required: false,
  },
  showTagsRight: {
    type: Boolean,
    required: false,
  },
  showTaskProgress: {
    type: Boolean,
    required: false,
  },
  deletable: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  searchMatches: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['click', 'delete'])
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const { getProjectTagChips, getSearchMatchChips } = useChips()

onMounted(async() => {
  if (props.showTags) {
    await portalStore.fetchTagsAsync({all: true}, true)
  }
})

const projectTags = ref(props.item?.tags ? props.item.tags.map(tag => portalStore.getTagById(tag)) : [])

const projectServices = ref(props.item?.services ? props.item.services.map(service => planningStore.getStaffById(service) || planningStore.getEquipmentById(service)).filter(service => !!service) : [])
const staff = ref(projectServices.value.filter(service => service.type === portalStore.serviceTypeEnum.STAFF))
const equipment = ref(projectServices.value.filter(service => service.type === portalStore.serviceTypeEnum.EQUIPMENT))

const statusLabel = ref(props.item?.status ? planningStore.getLocalizedProjectStatuses()[props.item?.status].name : null)
const statusColor = ref(props.item?.status ? planningStore.getProjectStatusColor(props.item?.status) : null)
const statusChips = [{ name: statusLabel.value, color: statusColor.value }]
const showStatus = !!props.item?.status && !props.hideStatus

</script>
<template>
  <q-item
    :key="props.item.id"
    :clickable="props.clickable"
    class="full-width"
    :class="{ 'dx-disabled-row': props.disabled }"
    @click="emit('click', props.item)"
  >
    <q-item-section
      :style="{ 'border-left': '.25rem solid', 'text-wrap': 'wrap', 'border-color': props.hideColor ? 'transparent' : props.item.color }"
      class="q-pl-sm"
    >
      <q-item-label
        v-if="!props.hideLabel"
        class="text-wrap"
      >
        {{ props.item.name }}
        <div
          v-if="props.item.listing"
          class="text-caption text-grey text-wrap"
        >{{ planningStore.getListings().find(l => l.id === props.item.listing)?.name }}</div>
      </q-item-label>
      <q-item-label
        v-if="showStatus && props.statusLeft"
        caption
      >
        <chip-template :values="statusChips" size="sm" />
      </q-item-label>
      <q-item-label
        v-if="props.searchMatches.length || (props.item.tag_ids?.length && !props.showTagsRight)"
        caption
      >
        <chip-template
          :values="[...getSearchMatchChips(props.searchMatches), ...(props.showTagsRight ? [] : getProjectTagChips(props.item.tag_ids))]"
          size="xs"
        />
      </q-item-label>
      <q-item-label
        v-if="props.showTaskProgress && props.item.task_progress?.total"
        caption
      >
        <task-progress-summary :progress="props.item.task_progress" />
      </q-item-label>
      <q-item-label
        v-if="props.showStaff"
        caption
        class="word-break"
      >
        {{ staff.sort(service => service.name).map(service => (`${planningStore.getServiceName(service)}`)).join(', ') }}
      </q-item-label>
      <q-item-label
        v-if="props.showEquipment"
        caption
        class="word-break"
      >
        {{ equipment.map(service => service.name).sort().join(', ') }}
      </q-item-label>
      <q-item-label
        v-if="props.showTags"
        caption
        class="word-break"
      >
        {{ projectTags.map(tag => tag.name).sort().join(', ') }}
      </q-item-label>
    </q-item-section>
    <q-item-section
      avatar
      class="align-middle align-center q-pa-none"
    >
      <q-item-label
        v-if="!props.hideTime"
        caption
        class="align-items-baseline"
      >
        <q-icon
          name="mdi-clock-outline"
          class="q-mr-xs"
        />
        <span>
          {{ dayjs(props.start || props.item.start).format('HH:mm') }} - {{ dayjs(props.end || props.item.end).format('HH:mm') }}
        </span>
      </q-item-label>
      <slot name="above-date" />
      <q-item-label
        v-if="!props.hideDate"
        caption
        class="align-items-baseline"
      >
        <q-icon
          name="mdi-calendar"
          class="q-mr-xs"
        />
        <span>
          {{ dayjs(props.start || props.item.start).format('DD/MM') }} {{ $t('common.to') }} {{ dayjs(props.end || props.item.end).format('DD/MM') }}
        </span>
      </q-item-label>
      <q-item-label
        v-if="props.date"
        caption
        class="align-items-baseline"
      >
        <q-icon
          name="mdi-calendar"
          class="q-mr-xs"
        />
        <span>
          {{ dayjs(props.date).format('DD/MM') }}
        </span>
      </q-item-label>
      <q-item-label
        v-if="showStatus && !props.statusLeft"
        caption
        class="align-items-middle"
      >
        <chip-template :values="statusChips" size="sm" />
      </q-item-label>
      <q-item-label
        v-if="props.showTagsRight && props.item.tag_ids?.length"
        caption
        class="align-items-middle"
      >
        <chip-template
          :values="getProjectTagChips(props.item.tag_ids)"
          size="xs"
        />
      </q-item-label>
    </q-item-section>
    <q-item-section
      v-for="warning in props.item.warnings"
      :key="warning"
      side
    >
      <q-icon
        name="mdi-alert"
        color="warning"
      />
    </q-item-section>
    <q-item-section
      v-if="props.deletable"
      side
    >
      <q-btn
        flat
        dense
        round
        icon="mdi-delete"
        color="negative"
        @click.stop="emit('delete', props.item)"
      />
    </q-item-section>
  </q-item>
</template>
<style lang="scss" scoped>
.word-break {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
